import styled from 'styled-components'

import { onDesktop } from '@/components/utils/dimension'

import type {
  SearchExperienceSwitchProps,
  SwitchButtonStyleProps,
} from './SearchExperienceSwitch.types'

const SwitchButton = styled.button<SwitchButtonStyleProps>`
  display: ${({ $placement }) =>
    $placement === 'traditional-desktop' ? 'none' : 'inline-flex'};
  min-height: ${({ $placement }) => ($placement === 'beta' ? '32px' : '44px')};
  align-items: center;
  justify-content: flex-start;
  gap: 7px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.ColorBrandPrimary};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: left;
  cursor: pointer;
  transition: color 150ms ease;

  ${({ $placement }) =>
    $placement === 'traditional-mobile' &&
    `
      width: 100%;
      margin-bottom: 2px;
    `}

  // Flows as text so the badge follows the wrapped label opposite the count.
  ${({ $placement }) =>
    $placement === 'traditional-inline' &&
    `
      display: inline-block;
      max-width: 160px;
      line-height: 18px;
      text-align: right;

      > span + span {
        margin-left: 6px;
        vertical-align: 1px;
      }
    `}

  ${({ $placement }) =>
    $placement === 'beta-compact' &&
    `
      min-height: 38px;
      gap: 5px;
      font-size: 12px;

      svg {
        width: 14px;
        height: 14px;
      }
    `}

  svg {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.ColorBrandPrimary};
    outline-offset: 3px;
    border-radius: 4px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }

  ${onDesktop} {
    display: ${({ $placement }) =>
      $placement === 'traditional-mobile' || $placement === 'traditional-inline'
        ? 'none'
        : 'inline-flex'};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const BetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #f2f5f7;
  color: ${({ theme }) => theme.ColorBrandPrimary};
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-decoration: none;
`

const ReturnIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M7.8 19v-2H14c1.575 0 2.504-.325 3.288-.975C18.096 15.375 18.5 14.533 18.5 13.5s-.404-1.875-1.212-2.525C16.504 10.325 15.575 10 14 10H7.8l2.6 2.6L9 14 4 9l5-5 1.4 1.4L7.8 8H14c2.425 0 3.829.525 5.213 1.575C20.404 10.625 21 11.933 21 13.5s-.596 2.875-1.787 3.925C17.829 18.475 16.425 19 14 19H7.8Z"
      fill="currentColor"
    />
  </svg>
)

export const SearchExperienceSwitch: React.FC<SearchExperienceSwitchProps> = ({
  busy = false,
  onClick,
  placement,
}) => {
  const isBetaOptIn = !placement.startsWith('beta')
  const label = isBetaOptIn ? 'Try the new search experience' : 'Exit beta'

  return (
    <SwitchButton
      type="button"
      $placement={placement}
      onClick={onClick}
      disabled={busy}
      aria-busy={busy}
    >
      {!isBetaOptIn && <ReturnIcon />}
      <span>{label}</span>
      {isBetaOptIn && <BetaBadge>Beta</BetaBadge>}
    </SwitchButton>
  )
}

export type { SearchExperienceSwitchPlacement } from './SearchExperienceSwitch.types'
